import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon, Sun, Moon, LogOut, Sparkles, Loader2, ChevronDown, Target, BookOpen, Settings2 } from 'lucide-react';
import { es } from 'date-fns/locale';

export function AppHeader({ 
  selectedDate, 
  onSelectDate, 
  theme, 
  onToggleTheme,
  onGenerateSummary,
  summaryLoading,
  onOpenPrompts,
}) {
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [aiMenuOpen, setAiMenuOpen] = useState(false);
  const aiMenuRef = useRef(null);
  const { logout } = useAuth();

  useEffect(() => {
    const handleClick = (e) => {
      if (aiMenuRef.current && !aiMenuRef.current.contains(e.target)) {
        setAiMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleDateSelect = (date) => {
    onSelectDate(date);
    setCalendarOpen(false);
  };

  const handleLogout = async () => {
    if (window.confirm('¿Seguro que quieres cerrar sesión?')) {
      await logout();
    }
  };

  const handleAiOption = (mode) => {
    setAiMenuOpen(false);
    onGenerateSummary(mode);
  };

  return (
    <header className="app-header" data-testid="app-header">
      <div className="header-inner">
        <h1 className="app-title brand-title" data-testid="app-title">BITACARIA</h1>

        <div className="header-actions">
          {/* AI Summary Dropdown */}
          <div className="ai-menu-wrapper" ref={aiMenuRef}>
            <button
              className="header-btn header-btn-ai"
              onClick={() => !summaryLoading && setAiMenuOpen(v => !v)}
              disabled={summaryLoading}
              title="Resúmenes IA"
              data-testid="ai-summary-menu-btn"
            >
              {summaryLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="hidden sm:inline ml-2">Generando...</span>
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4" />
                  <span className="hidden sm:inline ml-2">Resumen IA</span>
                  <ChevronDown className="h-3 w-3 ml-1" />
                </>
              )}
            </button>
            {aiMenuOpen && (
              <div className="ai-menu-dropdown" data-testid="ai-summary-dropdown">
                <button
                  className="ai-menu-item"
                  onClick={() => handleAiOption('general')}
                  data-testid="ai-summary-general-btn"
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  <span>Resumen General</span>
                </button>
                <button
                  className="ai-menu-item"
                  onClick={() => handleAiOption('objetivos')}
                  data-testid="ai-summary-objetivos-btn"
                >
                  <Target className="h-3.5 w-3.5" />
                  <span>Objetivos (so far)</span>
                </button>
                <button
                  className="ai-menu-item"
                  onClick={() => handleAiOption('bitacora')}
                  data-testid="ai-summary-bitacora-btn"
                >
                  <BookOpen className="h-3.5 w-3.5" />
                  <span>Bitácora (so far)</span>
                </button>
                <div className="ai-menu-divider" />
                <button
                  className="ai-menu-item ai-menu-item-secondary"
                  onClick={() => { setAiMenuOpen(false); onOpenPrompts(); }}
                  data-testid="ai-prompts-btn"
                >
                  <Settings2 className="h-3.5 w-3.5" />
                  <span>Configurar prompts</span>
                </button>
              </div>
            )}
          </div>

          <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
            <PopoverTrigger asChild>
              <button
                className="header-icon-btn"
                data-testid="date-picker-btn"
              >
                <CalendarIcon className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>

          <button
            className="header-icon-btn"
            onClick={onToggleTheme}
            data-testid="theme-toggle-btn"
          >
            {theme === 'dark' ? (
              <Sun className="h-4 w-4" />
            ) : (
              <Moon className="h-4 w-4" />
            )}
          </button>

          <button
            className="header-icon-btn"
            onClick={handleLogout}
            title="Cerrar sesión"
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
